/**
 * API Client
 *
 * Type-safe HTTP client for making requests to the backend API.
 * Provides automatic JSON handling, error normalization, and credentials management.
 */

import type { paths } from '@access-cms/api-types';
import { HttpMethod } from '@access-cms/shared-constants';

import type {
  ApiError,
  ApiPaths,
  PathMethods,
  PathParams,
  QueryParamRecord,
  QueryParams,
  RequestBody,
  SuccessResponse,
} from './types';
import { buildPath, buildUrl } from './types';

// =============================================================================
// Configuration
// =============================================================================

/**
 * Configuration options for the API client
 */
export interface ApiClientConfig {
  /** Base URL for API requests */
  baseUrl?: string;
  /** Default headers to include in all requests */
  defaultHeaders?: HeadersInit;
}

let globalConfig: ApiClientConfig = {
  baseUrl: '',
  defaultHeaders: {
    'Content-Type': 'application/json',
  },
};

/**
 * Configure the API client globally
 */
export function configureApiClient(config: Partial<ApiClientConfig>): void {
  globalConfig = { ...globalConfig, ...config };
}

/**
 * Get the current API client configuration
 */
export function getApiClientConfig(): ApiClientConfig {
  return { ...globalConfig };
}

// =============================================================================
// Error Handling
// =============================================================================

/**
 * Custom error class for API errors
 */
export class ApiRequestError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
    public errors?: ApiError['errors'],
    public responseBody?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  static fromResponse(data: ApiError, statusCode: number): ApiRequestError {
    const message = data.message || data.meta?.message || 'An unexpected error occurred';
    const code = data.code || data.meta?.errorCode;
    const errors = data.errors || data.meta?.errors;

    return new ApiRequestError(message, statusCode, code, errors, data as unknown as Record<string, unknown>);
  }
}

// =============================================================================
// Request Function Types
// =============================================================================

interface RequestOptions<P extends ApiPaths, M extends PathMethods<P>> {
  body?: RequestBody<P, M>;
  headers?: HeadersInit;
  method: M;
  path: P;
  pathParams?: PathParams<P, M>;
  queryParams?: QueryParams<P, M>;
  signal?: AbortSignal;
}

/**
 * Merge default and per-request headers. Omits Content-Type when there is no body
 * so bodyless DELETE/GET requests are not rejected by Fastify (FST_ERR_CTP_EMPTY_JSON_BODY).
 */
function mergeRequestHeaders(
  defaultHeaders: HeadersInit | undefined,
  customHeaders: HeadersInit | undefined,
  includeContentType: boolean,
): Headers {
  const merged = new Headers(defaultHeaders);

  if (customHeaders) {
    new Headers(customHeaders).forEach((value, key) => {
      merged.set(key, value);
    });
  }

  if (!includeContentType) {
    merged.delete('Content-Type');
  }

  return merged;
}

/**
 * Headers for GET/DELETE (and other requests) without a JSON body.
 * Omits Content-Type so Fastify does not reject empty bodies (FST_ERR_CTP_EMPTY_JSON_BODY).
 */
export function getBodylessRequestHeaders(customHeaders?: HeadersInit): Headers {
  return mergeRequestHeaders(getApiClientConfig().defaultHeaders, customHeaders, false);
}

// =============================================================================
// Core Request Function
// =============================================================================

/**
 * Make a type-safe API request
 *
 * @example
 * ```ts
 * // GET request with path params
 * const user = await apiRequest({
 *   path: '/api/v1/users/{id}',
 *   method: HttpMethod.GET,
 *   pathParams: { id: '123' },
 * });
 *
 * // POST request with body
 * const newUser = await apiRequest({
 *   path: '/api/v1/users',
 *   method: HttpMethod.POST,
 *   body: { email: 'test@example.com', userType: 'CONTRACTOR' },
 * });
 * ```
 */
export async function apiRequest<P extends ApiPaths, M extends PathMethods<P>>(
  options: RequestOptions<P, M>,
): Promise<SuccessResponse<P, M>> {
  const { path, method, pathParams, queryParams, body, headers, signal } = options;
  const config = getApiClientConfig();

  // Build the full URL
  const resolvedPath = buildPath(path, pathParams as Record<string, string | number>);
  const fullUrl = buildUrl(`${config.baseUrl}${resolvedPath}`, queryParams as QueryParamRecord);

  const hasRequestBody = body !== undefined && method !== HttpMethod.GET;

  const requestInit: RequestInit = {
    credentials: 'include', // Include cookies for auth
    headers: mergeRequestHeaders(config.defaultHeaders, headers, hasRequestBody),
    method: method.toUpperCase(),
    signal,
  };

  if (hasRequestBody) {
    requestInit.body = JSON.stringify(body);
  }

  // Make the request
  const response = await fetch(fullUrl, requestInit);

  // Handle no-content responses (204)
  if (response.status === 204) {
    return undefined as SuccessResponse<P, M>;
  }

  // Parse response body
  const data = await response.json();

  // Handle error responses
  if (!response.ok) {
    throw ApiRequestError.fromResponse(data, response.status);
  }

  return data as SuccessResponse<P, M>;
}

// =============================================================================
// Convenience Methods
// =============================================================================

// These type utilities filter paths to only those supporting specific HTTP methods
type GetPaths = {
  [P in ApiPaths]: paths[P] extends { get: object } ? P : never;
}[ApiPaths];

type PostPaths = {
  [P in ApiPaths]: paths[P] extends { post: object } ? P : never;
}[ApiPaths];

type PatchPaths = {
  [P in ApiPaths]: paths[P] extends { patch: object } ? P : never;
}[ApiPaths];

type PutPaths = {
  [P in ApiPaths]: paths[P] extends { put: object } ? P : never;
}[ApiPaths];

type DeletePaths = {
  [P in ApiPaths]: paths[P] extends { delete: object } ? P : never;
}[ApiPaths];

/**
 * GET request helper
 */
export async function get<P extends GetPaths>(
  path: P,
  options?: {
    pathParams?: PathParams<P, 'get'>;
    queryParams?: QueryParams<P, 'get'>;
    signal?: AbortSignal;
  },
): Promise<SuccessResponse<P, 'get'>> {
  return apiRequest({
    method: HttpMethod.GET,
    path,
    pathParams: options?.pathParams,
    queryParams: options?.queryParams,
    signal: options?.signal,
  });
}

/**
 * POST request helper
 */
export async function post<P extends PostPaths>(
  path: P,
  body: RequestBody<P, 'post'>,
  options?: {
    pathParams?: PathParams<P, 'post'>;
    queryParams?: QueryParams<P, 'post'>;
    signal?: AbortSignal;
  },
): Promise<SuccessResponse<P, 'post'>> {
  return apiRequest({
    body,
    method: HttpMethod.POST,
    path,
    pathParams: options?.pathParams,
    queryParams: options?.queryParams,
    signal: options?.signal,
  });
}

/**
 * PATCH request helper
 */
export async function patch<P extends PatchPaths>(
  path: P,
  body: RequestBody<P, 'patch'>,
  options?: {
    pathParams?: PathParams<P, 'patch'>;
    queryParams?: QueryParams<P, 'patch'>;
    signal?: AbortSignal;
  },
): Promise<SuccessResponse<P, 'patch'>> {
  return apiRequest({
    body,
    method: HttpMethod.PATCH,
    path,
    pathParams: options?.pathParams,
    queryParams: options?.queryParams,
    signal: options?.signal,
  });
}

/**
 * PUT request helper
 */
export async function put<P extends PutPaths>(
  path: P,
  body: RequestBody<P, 'put'>,
  options?: {
    pathParams?: PathParams<P, 'put'>;
    queryParams?: QueryParams<P, 'put'>;
    signal?: AbortSignal;
  },
): Promise<SuccessResponse<P, 'put'>> {
  return apiRequest({
    body,
    method: HttpMethod.PUT,
    path,
    pathParams: options?.pathParams,
    queryParams: options?.queryParams,
    signal: options?.signal,
  });
}

/**
 * DELETE request helper
 */
export async function del<P extends DeletePaths>(
  path: P,
  options?: {
    pathParams?: PathParams<P, 'delete'>;
    // Permissive record rather than QueryParams<P,'delete'>: DELETE query params are
    // sparsely modelled in the generated spec, and callers (e.g. the roster removal
    // date) need to pass them before the next openapi regen picks up the schema.
    queryParams?: QueryParamRecord;
    signal?: AbortSignal;
  },
): Promise<SuccessResponse<P, 'delete'>> {
  return apiRequest({
    method: HttpMethod.DELETE,
    path,
    pathParams: options?.pathParams,
    queryParams: options?.queryParams as never,
    signal: options?.signal,
  });
}

// =============================================================================
// Roster removal helpers
// =============================================================================

/**
 * Remove a roster member. Per the offboarding design the caller supplies a removal
 * date: a future date files a scheduled removal (JOB B offboards it when due), while
 * today/past (or omitting it) removes immediately. Entity Manager branches server-side.
 */
export async function removeRosterMember(
  contractId: string,
  userId: string,
  options?: { scheduledRemovalDate?: string; signal?: AbortSignal },
): Promise<void> {
  await del('/api/v1/contracts/{contractId}/roster/{userId}', {
    pathParams: { contractId, userId },
    queryParams: options?.scheduledRemovalDate ? { scheduledRemovalDate: options.scheduledRemovalDate } : undefined,
    signal: options?.signal,
  });
}

/**
 * Cancel a pending scheduled roster removal (clears `scheduled_removal_date`; the member
 * stays ACTIVE).
 */
export async function cancelScheduledRemoval(
  contractId: string,
  userId: string,
  options?: { signal?: AbortSignal },
): Promise<void> {
  await del('/api/v1/contracts/{contractId}/roster/{userId}/scheduled-removal', {
    pathParams: { contractId, userId },
    signal: options?.signal,
  });
}

// =============================================================================
// Raw (non-JSON) Requests
// =============================================================================

/** A file served as a download, plus the name the server suggested for it. */
export interface ApiFileResponse {
  blob: Blob;

  /** Parsed from Content-Disposition; null when the server did not supply one. */
  filename: string | null;

  /** Response headers, so callers can read metadata the server sent alongside. */
  headers: Headers;
}

/**
 * Options for a raw request.
 *
 * `path` is a plain string rather than a generated `paths` key: these endpoints
 * serve files, not JSON, so they have no typed response to bind to.
 */
export interface ApiFileRequestOptions {
  path: string;
  pathParams?: Record<string, string | number>;
  queryParams?: QueryParamRecord;
  headers?: HeadersInit;
  signal?: AbortSignal;

  /** Content-Type the response must carry, as a guard against silent redirects. */
  expectedContentType?: string;
}

const CONTENT_DISPOSITION_FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

/** Pull the download filename out of a Content-Disposition header. */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) {
    return null;
  }

  const match = CONTENT_DISPOSITION_FILENAME.exec(header);

  return match ? decodeURIComponent(match[1].trim()) : null;
}

/**
 * Fetch an endpoint that responds with a file rather than JSON.
 *
 * Errors still arrive as the standard JSON envelope, so a failure is normalized
 * into {@link ApiRequestError} exactly as it is for JSON requests.
 *
 * @example
 * ```typescript
 * const { blob, filename } = await apiRequestFile({
 *   path: '/api/v1/contracts/{contractId}/roster/export',
 *   pathParams: { contractId },
 *   queryParams: { rosterStatus: 'ACTIVE' },
 * });
 * ```
 */
export async function apiRequestFile(options: ApiFileRequestOptions): Promise<ApiFileResponse> {
  const { path, pathParams, queryParams, headers, signal, expectedContentType = 'text/csv' } = options;
  const config = getApiClientConfig();

  let resolvedPath = path;

  for (const [key, value] of Object.entries(pathParams ?? {})) {
    resolvedPath = resolvedPath.replace(`{${key}}`, encodeURIComponent(String(value)));
  }

  const fullUrl = buildUrl(`${config.baseUrl}${resolvedPath}`, queryParams);

  const response = await fetch(fullUrl, {
    credentials: 'include',
    headers: mergeRequestHeaders(config.defaultHeaders, headers, false),
    method: HttpMethod.GET.toUpperCase(),
    signal,
  });

  if (!response.ok) {
    // Failures usually come back as the JSON error envelope — but a gateway can
    // answer with an empty body or an HTML page, so fall back to a synthetic
    // envelope rather than letting fromResponse dereference undefined.
    let errorBody: ApiError | undefined;

    try {
      errorBody = (await response.json()) as ApiError;
    } catch {
      errorBody = undefined;
    }

    throw ApiRequestError.fromResponse(errorBody ?? ({ message: response.statusText } as ApiError), response.status);
  }

  // A 200 is not proof we got the file. An expired session redirects to the IdP,
  // which fetch follows transparently and answers 200 text/html — saving that as
  // ".csv" would hand the user a login page named like their data.
  const contentType = response.headers.get('Content-Type') ?? '';

  if (!contentType.includes(expectedContentType)) {
    throw new ApiRequestError(
      'The server did not return the expected file. Your session may have expired.',
      response.status,
      'UNEXPECTED_CONTENT_TYPE',
    );
  }

  return {
    blob: await response.blob(),
    filename: parseContentDispositionFilename(response.headers.get('Content-Disposition')),
    headers: response.headers,
  };
}

// =============================================================================
// Export client object for namespaced usage
// =============================================================================

export const apiClient = {
  delete: del,
  file: apiRequestFile,
  get,
  patch,
  post,
  request: apiRequest,
};
